import { Link, useLocation } from "react-router-dom";

import { useAuthStore } from "@/state/authStore";

interface SidebarItem {
  to: string;
  label: string;
  Icon: (props: { className?: string }) => JSX.Element;
  isActive: (pathname: string) => boolean;
  /** Só administradores veem o item (F1.21). Não-admin fica só com "Arquivos". */
  adminOnly?: boolean;
}

function FilesIcon({ className }: { className?: string }) {
  return (
    <svg viewBox="0 0 24 24" fill="none" className={className} aria-hidden="true">
      <path d="M4 7.5A1.5 1.5 0 0 1 5.5 6H10l2 2h6.5A1.5 1.5 0 0 1 20 9.5v8a1.5 1.5 0 0 1-1.5 1.5h-13A1.5 1.5 0 0 1 4 17.5v-10Z" stroke="currentColor" strokeWidth="1.5" />
    </svg>
  );
}

function QueueIcon({ className }: { className?: string }) {
  return (
    <svg viewBox="0 0 24 24" fill="none" className={className} aria-hidden="true">
      <path d="M7 6h10M7 12h10M7 18h10M4 6h.01M4 12h.01M4 18h.01" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
    </svg>
  );
}

function InsightsIcon({ className }: { className?: string }) {
  return (
    <svg viewBox="0 0 24 24" fill="none" className={className} aria-hidden="true">
      <path d="M5 19V9m7 10V5m7 14v-7" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
    </svg>
  );
}

function AuditIcon({ className }: { className?: string }) {
  return (
    <svg viewBox="0 0 24 24" fill="none" className={className} aria-hidden="true">
      <path d="M9 4h6v3H9V4ZM7 5.5H5.5A1.5 1.5 0 0 0 4 7v12.5A1.5 1.5 0 0 0 5.5 21h13a1.5 1.5 0 0 0 1.5-1.5V7a1.5 1.5 0 0 0-1.5-1.5H17M8 12h8M8 16h5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

function UsersIcon({ className }: { className?: string }) {
  return (
    <svg viewBox="0 0 24 24" fill="none" className={className} aria-hidden="true">
      <path d="M16.5 19a4.5 4.5 0 0 0-9 0M15 8.5a3 3 0 1 1-6 0 3 3 0 0 1 6 0Zm6 10.5a3.5 3.5 0 0 0-5-3.163M18.5 8a2.5 2.5 0 1 1-1.732 4.303" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
    </svg>
  );
}

function TrialIcon({ className }: { className?: string }) {
  return (
    <svg viewBox="0 0 24 24" fill="none" className={className} aria-hidden="true">
      <path d="M12 21a7.5 7.5 0 1 0 0-15 7.5 7.5 0 0 0 0 15ZM12 9.5v4l2.5 1.5M10 3h4M18.5 6.5l1.5-1.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

function SettingsIcon({ className }: { className?: string }) {
  return (
    <svg viewBox="0 0 24 24" fill="none" className={className} aria-hidden="true">
      <path d="M12 15a3 3 0 1 0 0-6 3 3 0 0 0 0 6Z" stroke="currentColor" strokeWidth="1.5" />
      <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 1 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 1 1-4 0v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 1 1-2.83-2.83l.06-.06A1.65 1.65 0 0 0 4.6 15a1.65 1.65 0 0 0-1.51-1H3a2 2 0 1 1 0-4h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 1 1 2.83-2.83l.06.06A1.65 1.65 0 0 0 9 4.6a1.65 1.65 0 0 0 1-1.51V3a2 2 0 1 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 1 1 2.83 2.83l-.06.06A1.65 1.65 0 0 0 19.4 9v0a1.65 1.65 0 0 0 1.51 1H21a2 2 0 1 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1Z" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

function UsageIcon({ className }: { className?: string }) {
  return (
    <svg viewBox="0 0 24 24" fill="none" className={className} aria-hidden="true">
      <rect x="4" y="4" width="4.5" height="4.5" rx="1" fill="currentColor" opacity="0.35" />
      <rect x="9.75" y="4" width="4.5" height="4.5" rx="1" fill="currentColor" opacity="0.7" />
      <rect x="15.5" y="4" width="4.5" height="4.5" rx="1" fill="currentColor" />
      <rect x="4" y="9.75" width="4.5" height="4.5" rx="1" fill="currentColor" opacity="0.7" />
      <rect x="9.75" y="9.75" width="4.5" height="4.5" rx="1" fill="currentColor" />
      <rect x="15.5" y="9.75" width="4.5" height="4.5" rx="1" fill="currentColor" opacity="0.35" />
      <rect x="4" y="15.5" width="4.5" height="4.5" rx="1" fill="currentColor" />
      <rect x="9.75" y="15.5" width="4.5" height="4.5" rx="1" fill="currentColor" opacity="0.35" />
      <rect x="15.5" y="15.5" width="4.5" height="4.5" rx="1" fill="currentColor" opacity="0.7" />
    </svg>
  );
}

function DictionaryIcon({ className }: { className?: string }) {
  return (
    <svg viewBox="0 0 24 24" fill="none" className={className} aria-hidden="true">
      <path d="M5 5.5A1.5 1.5 0 0 1 6.5 4H19v14H6.5A1.5 1.5 0 0 0 5 19.5v-14ZM5 19.5A1.5 1.5 0 0 0 6.5 21H19v-3M9 8h6M9 11.5h4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

function ApiIcon({ className }: { className?: string }) {
  return (
    <svg viewBox="0 0 24 24" fill="none" className={className} aria-hidden="true">
      <path d="M8 9l-3 3 3 3M16 9l3 3-3 3M13 6l-2 12" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

const SIDEBAR_ITEMS: SidebarItem[] = [
  {
    to: "/manager",
    label: "Arquivos",
    Icon: FilesIcon,
    isActive: (pathname) => pathname === "/manager" || pathname.startsWith("/f/"),
  },
  {
    to: "/queue",
    label: "Fila de Renders",
    Icon: QueueIcon,
    isActive: (pathname) => pathname === "/queue",
    adminOnly: true,
  },
  {
    to: "/insights",
    label: "Insights",
    Icon: InsightsIcon,
    isActive: (pathname) => pathname === "/insights",
    adminOnly: true,
  },
  {
    to: "/uso",
    label: "Uso",
    Icon: UsageIcon,
    isActive: (pathname) => pathname === "/uso",
    adminOnly: true,
  },
  {
    to: "/audit",
    label: "Auditoria",
    Icon: AuditIcon,
    isActive: (pathname) => pathname === "/audit",
    adminOnly: true,
  },
  {
    to: "/users",
    label: "Usuarios",
    Icon: UsersIcon,
    isActive: (pathname) => pathname === "/users",
    adminOnly: true,
  },
  {
    to: "/testes-pro",
    label: "Testes do Pro",
    Icon: TrialIcon,
    isActive: (pathname) => pathname === "/testes-pro",
    adminOnly: true,
  },
  {
    to: "/settings",
    label: "Configuracoes",
    Icon: SettingsIcon,
    isActive: (pathname) => pathname === "/settings",
    adminOnly: true,
  },
  {
    to: "/dicionario",
    label: "Dicionário",
    Icon: DictionaryIcon,
    isActive: (pathname) => pathname === "/dicionario",
    adminOnly: true,
  },
  {
    to: "/docs/api",
    label: "API v1",
    Icon: ApiIcon,
    isActive: (pathname) => pathname.startsWith("/docs/api"),
    adminOnly: true,
  },
];

interface AppSidebarProps {
  onNavigate?: () => void;
}

export function AppSidebar({ onNavigate }: AppSidebarProps) {
  const { pathname } = useLocation();
  const isAdmin = useAuthStore((state) => state.user?.is_admin === true);
  const items = isAdmin ? SIDEBAR_ITEMS : SIDEBAR_ITEMS.filter((item) => !item.adminOnly);

  return (
    <nav className="flex h-full flex-col gap-1 p-2" aria-label="Navegacao principal">
      {items.map((item) => {
        const active = item.isActive(pathname);
        const Icon = item.Icon;
        return (
          <Link
            key={item.to}
            to={item.to}
            onClick={onNavigate}
            aria-current={active ? "page" : undefined}
            className={[
              "group flex items-center gap-2 rounded-md border px-3 py-2 text-sm transition",
              "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-emerald-500",
              active
                ? "border-emerald-600 bg-emerald-600/15 text-emerald-300"
                : "border-transparent text-ink-300 hover:border-ink-700 hover:bg-ink-800 hover:text-ink-100",
            ].join(" ")}
          >
            <Icon className="h-4 w-4 shrink-0" />
            <span>{item.label}</span>
          </Link>
        );
      })}
    </nav>
  );
}
